import { useEffect, useState } from "react";
import { BookOpen, GraduationCap, Puzzle, Check, Loader2, Users, MessageCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

const GUIDES = {
  "8-10": {
    title: "Ages 8–10 · Tweens",
    overview: "Big feelings, black-and-white thinking, and a growing wish for independence — but still very much your child. Play is still how they process the world.",
    tips: ["Keep routines predictable; they feel safe.", "Name feelings out loud to build their vocabulary.", "Co-view screens and talk about what they watch.", "Praise effort, not just outcomes.", "Answer questions honestly, in small age-right doses."],
    watch: ["Sudden fear of school or a friend", "New words or jokes they don't understand themselves", "Copying older siblings' phones/games"],
    say: ["“Tell me about the best and worst part of your day.”", "“It's okay to feel two things at once.”", "“Let's figure this out together.”"],
    common: ["First group chats", "Roblox / Minecraft culture", "Wanting a phone 'because everyone has one'"],
  },
  "11-13": {
    title: "Ages 11–13 · Early teens",
    overview: "Friendships become central and self-consciousness spikes. They test boundaries while still needing them. Identity starts forming online and off.",
    tips: ["Ask about their world with curiosity, not interrogation.", "Expect eye-rolls — stay warm and consistent.", "Agree phone norms together rather than dictating.", "Notice and name the good moments.", "Keep talking about bodies, consent and kindness early."],
    watch: ["Big mood swings tied to friend drama", "Secrecy about apps or accounts", "Comparing themselves to others online"],
    say: ["“I'm not here to judge — I just want to understand.”", "“What do your friends think about it?”", "“You can always tell me anything, even hard things.”"],
    common: ["Snapchat streaks & group chats", "Meme and TikTok humour", "First crushes and 'talking stages'"],
  },
  "14-16": {
    title: "Ages 14–16 · Mid teens",
    overview: "Identity, belonging and autonomy drive most behaviour. Risk-taking, strong opinions and privacy are developmentally normal, not rejection of you.",
    tips: ["Pick your battles; keep the relationship first.", "Offer choices within firm, few limits.", "Listen twice as much as you talk.", "Be a calm, non-shaming place to land.", "Stay interested in what they love, even if it's not your thing."],
    watch: ["Withdrawing from the whole family for weeks", "Big changes in sleep, appetite or friends", "Signs of comparison, pressure or online conflict"],
    say: ["“I trust you — and I'm here if a plan goes sideways.”", "“Help me understand why this matters to you.”", "“You don't have to have it all figured out.”"],
    common: ["Identity & self-expression", "Late-night messaging", "Strong stance on fairness and causes"],
  },
  "17-19": {
    title: "Ages 17–19 · Older teens",
    overview: "Preparing to launch. They want to be treated as near-adults while still needing your steadiness in the background.",
    tips: ["Coach, don't manage — let them own decisions.", "Stay available without hovering.", "Talk openly about money, safety and consent.", "Trust is built by respecting growing independence.", "Let natural consequences teach where it's safe."],
    watch: ["Overwhelm about the future or exams", "Isolating or masking stress", "Big life decisions made in secret"],
    say: ["“What feels like the right next step to you?”", "“I'm proud of who you're becoming.”", "“My door's always open, no matter what.”"],
    common: ["Future & exam pressure", "Independence and part-time work", "Deeper relationships"],
  },
};

const ARTICLES = [
  { slug: "brain", title: "Why teens think differently", teaser: "The teenage brain rewires from back to front — emotion leads, judgement follows.", body: "The prefrontal cortex, which handles planning and impulse control, keeps developing into the mid-twenties. Meanwhile the emotional brain is in full swing. This mismatch is why a calm teen can flip in seconds — it's biology, not defiance. Responding to the feeling first, then the facts, works with their brain rather than against it. Try 'name it to tame it': gently label the emotion you see before you problem-solve." },
  { slug: "belonging", title: "The pull of belonging", teaser: "For Gen Z, peer acceptance can feel like survival. Here's why — and how to help.", body: "Adolescents are wired to seek status and belonging among peers. Online, this plays out in likes, streaks and group chats. When you understand that fitting in feels existential, their choices make more sense. You can be the secure base they return to, no matter what happens in the group chat. Keep your reactions calm so home stays the safe place." },
  { slug: "screens", title: "Screens without the war", teaser: "Move from policing to partnership with a few small shifts.", body: "Rather than surveillance, aim for shared agreements: where phones charge overnight, tech-free meals, and open talk about what they see. Co-creating rules gives teens ownership, which massively improves follow-through — and keeps you as ally, not warden. Curiosity ('show me what's funny about this') beats confiscation." },
  { slug: "identity", title: "Identity, labels and finding themselves", teaser: "Trying on identities is how teens figure out who they are.", body: "Experimenting with style, interests, labels and views is healthy developmental work, not a verdict on your parenting. Stay curious and steady; you don't have to understand every trend to show you respect their search. What they need most is to feel accepted while they figure it out." },
  { slug: "risk", title: "Risk-taking is a feature, not a bug", teaser: "The same wiring that worries you also drives courage and growth.", body: "Teens are primed to seek novelty and reward — that's how they learn independence. Instead of only warning, help them think through consequences and plan safer versions. 'What's your backup if that goes wrong?' builds judgement better than a flat no." },
  { slug: "conflict", title: "Turning fights into conversations", teaser: "Most arguments are bids for autonomy or respect in disguise.", body: "When a small thing explodes, it's often really about feeling controlled or unheard. Lower the temperature: agree you both want the same goal, reflect their feeling, then problem-solve together. Repair after a blow-up matters more than never blowing up." },
  { slug: "listen", title: "How to really listen", teaser: "Teens open up sideways — in cars, late at night, mid-activity.", body: "Undivided attention can feel like pressure. Many teens talk best shoulder-to-shoulder rather than face-to-face — driving, walking, gaming, cooking. Follow their lead, resist fixing straight away, and let silences breathe. Being heard is often the whole point." },
];

const QUIZ = [
  { q: "When something's wrong, you'd rather…", a: ["Talk it through right away", "Give it space and time"] },
  { q: "You tend to show love by…", a: ["Words and encouragement", "Doing things and being present"] },
  { q: "In a disagreement you…", a: ["Address it head-on", "Cool down first, then talk"] },
  { q: "Big feelings are best…", a: ["Said out loud and named", "Felt quietly, then shared"] },
  { q: "When making plans you like…", a: ["A clear structure", "Room to go with the flow"] },
];

export default function Learn() {
  const [tab, setTab] = useState("guides");
  const [band, setBand] = useState("11-13");
  const [openArticle, setOpenArticle] = useState(null);

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-8 items-start" data-testid="learn-page">
      <aside className="lg:sticky lg:top-24 space-y-4">
        <div>
          <h1 className="font-serif text-[28px] sm:text-[34px] font-semibold">Learn &amp; understand</h1>
          <p style={{ color: "var(--text-muted)" }}>Age guides, the “why they think differently” library, and a style quiz.</p>
        </div>
        <div className="flex flex-col gap-2">
          {[{ id: "guides", label: "Age-stage guides", icon: GraduationCap }, { id: "library", label: "Library", icon: BookOpen }, { id: "quiz", label: "Style quiz", icon: Puzzle }].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`learn-tab-${t.id}`} className={`btn justify-start ${tab === t.id ? "btn-secondary" : "btn-outline"}`}><t.icon size={16} /> {t.label}</button>
          ))}
        </div>
      </aside>

      <div className="space-y-4">
        {tab === "guides" && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {Object.keys(GUIDES).map((b) => <button key={b} className={`btn ${band === b ? "btn-primary" : "btn-outline"}`} onClick={() => setBand(b)} data-testid={`guide-band-${b}`}>{b}</button>)}
            </div>
            <div className="dk-card p-6 space-y-4 anim-in">
              <h2 className="font-serif text-[24px] font-medium">{GUIDES[band].title}</h2>
              <p style={{ color: "var(--text-muted)" }}>{GUIDES[band].overview}</p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs uppercase tracking-wide mb-2" style={{ color: "var(--sage)" }}>What helps</p>
                  <ul className="space-y-2">{GUIDES[band].tips.map((t, i) => <li key={i} className="flex gap-2 text-[15px]"><Check size={18} style={{ color: "var(--success)" }} className="shrink-0" /> {t}</li>)}</ul>
                </div>
                <div className="space-y-5">
                  <div>
                    <p className="text-xs uppercase tracking-wide mb-2 flex items-center gap-1" style={{ color: "var(--warning)" }}><AlertTriangle size={13} /> Gently watch for</p>
                    <ul className="space-y-1 text-[15px]">{GUIDES[band].watch.map((t, i) => <li key={i}>• {t}</li>)}</ul>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide mb-2 flex items-center gap-1" style={{ color: "var(--accent)" }}><MessageCircle size={13} /> Try saying</p>
                    <ul className="space-y-1 text-[15px]">{GUIDES[band].say.map((t, i) => <li key={i} className="italic">{t}</li>)}</ul>
                  </div>
                </div>
              </div>
              <div className="rounded-xl p-4" style={{ background: "var(--sage-tint)" }}>
                <p className="text-xs uppercase tracking-wide mb-1" style={{ color: "var(--sage)" }}>Common at this age</p>
                <div className="flex flex-wrap gap-2 mt-1">{GUIDES[band].common.map((c, i) => <span key={i} className="badge-soft" style={{ background: "var(--card)" }}>{c}</span>)}</div>
              </div>
            </div>
          </div>
        )}

        {tab === "library" && (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {ARTICLES.map((a) => (
              <button key={a.slug} className="dk-card p-6 text-left hover:border-[#C85A48] transition-colors" onClick={() => setOpenArticle(a)} data-testid={`article-${a.slug}`}>
                <BookOpen size={20} style={{ color: "var(--accent)" }} />
                <h3 className="font-serif text-[18px] font-medium mt-2">{a.title}</h3>
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>{a.teaser}</p>
              </button>
            ))}
            {openArticle && (
              <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(47,58,52,.35)" }} onClick={() => setOpenArticle(null)}>
                <div className="dk-card p-8 max-w-lg space-y-4 anim-in" onClick={(e) => e.stopPropagation()}>
                  <h2 className="font-serif text-[24px] font-medium">{openArticle.title}</h2>
                  <p style={{ color: "var(--text-muted)" }}>{openArticle.body}</p>
                  <button className="btn btn-outline" onClick={() => setOpenArticle(null)}>Close</button>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "quiz" && <StyleQuiz />}
      </div>
    </div>
  );
}

function StyleQuiz() {
  const [children, setChildren] = useState([]);
  const [child, setChild] = useState(null);
  const [step, setStep] = useState(0);
  const [parentAns, setParentAns] = useState([]);
  const [childAns, setChildAns] = useState([]);
  const [who, setWho] = useState("parent");
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/children").then(({ data }) => { setChildren(data.children); if (data.children[0]) setChild(data.children[0]); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const style = (ans) => ans.filter((x) => x === 0).length >= Math.ceil(QUIZ.length / 2) ? "Direct & verbal" : "Reflective & present";

  const answer = (i) => {
    if (who === "parent") { const n = [...parentAns, i]; setParentAns(n); if (n.length === QUIZ.length) { setWho("child"); setStep(0); } else setStep(step + 1); }
    else { const n = [...childAns, i]; setChildAns(n); if (n.length === QUIZ.length) setDone(true); else setStep(step + 1); }
  };

  const save = async () => {
    if (!child) { toast.error("Add a child first to save this"); return; }
    setSaving(true);
    const p = style(parentAns), c = style(childAns);
    try {
      await api.post(`/children/${child.id}/quiz`, { parent_style: p, child_style: c, summary: p === c ? "You share a similar style — lean into it." : "You differ — meet in the middle with patience." });
      toast.success("Saved to " + (child.nickname || "child"));
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const restart = () => { setStep(0); setParentAns([]); setChildAns([]); setWho("parent"); setDone(false); };
  const withQuiz = children.filter((c) => c.quiz);

  return (
    <div className="space-y-4">
      {children.length > 0 && (
        <div className="dk-raised rounded-xl p-3 flex items-center gap-2 flex-wrap">
          <span className="text-sm">Taking for:</span>
          {children.map((c) => <button key={c.id} className={`badge-soft ${child?.id === c.id ? "" : "opacity-70"}`} style={child?.id === c.id ? { background: "var(--sage)", color: "#fff" } : {}} onClick={() => setChild(c)}>{c.nickname || c.age_band}</button>)}
        </div>
      )}

      {!done ? (
        <div className="dk-card p-6 space-y-4 anim-in" data-testid="quiz-card">
          <span className="badge-soft">{who === "parent" ? "About you" : "About your child"} · {step + 1}/{QUIZ.length}</span>
          <h3 className="font-serif text-[20px] font-medium">{QUIZ[step].q}</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {QUIZ[step].a.map((opt, i) => <button key={i} className="dk-card p-4 text-left hover:border-[#C85A48]" onClick={() => answer(i)} data-testid={`quiz-option-${i}`}>{opt}</button>)}
          </div>
        </div>
      ) : (
        <div className="dk-card p-6 space-y-4 anim-in" data-testid="quiz-result">
          <h3 className="font-serif text-[22px] font-medium">Your styles</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl p-4" style={{ background: "var(--card-alt)" }}><span className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>You</span><p className="font-serif text-[18px]">{style(parentAns)}</p></div>
            <div className="rounded-xl p-4" style={{ background: "var(--sage-tint)" }}><span className="text-xs uppercase tracking-wide" style={{ color: "var(--sage)" }}>Your child</span><p className="font-serif text-[18px]">{style(childAns)}</p></div>
          </div>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>{style(parentAns) === style(childAns) ? "You share a similar style — that shared language is a gift; use it." : "You have different styles. That's normal — the bridge is patience: match their pace before your point."}</p>
          <div className="flex gap-2">
            <button className="btn btn-primary" disabled={saving} onClick={save} data-testid="quiz-save-btn">{saving && <Loader2 size={16} className="animate-spin" />} Save to child</button>
            <button className="btn btn-outline" onClick={restart}>Retake</button>
          </div>
        </div>
      )}

      {withQuiz.length > 1 && (
        <div className="dk-card p-6 space-y-3" data-testid="quiz-compare">
          <div className="flex items-center gap-2"><Users size={18} style={{ color: "var(--accent)" }} /><h3 className="font-serif text-[20px] font-medium">Compare your kids</h3></div>
          <div className="grid sm:grid-cols-2 gap-3">
            {withQuiz.map((c) => (
              <div key={c.id} className="rounded-xl p-4" style={{ background: "var(--card-alt)" }}>
                <p className="font-medium">{c.nickname || "Child"} · {c.age_band}</p>
                <p className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>{c.quiz.summary}</p>
                <div className="text-sm">You: {c.quiz.parent_style}</div>
                <div className="text-sm" style={{ color: "var(--sage)" }}>Them: {c.quiz.child_style}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
